// src/pages/reports/ProductMarginReport.jsx
import { useState, useEffect, useMemo } from 'react'
import { ChevronDown, X } from 'lucide-react'
import BaseReportPage from '../../components/reports/BaseReportPage'
import BranchService from '../../services/branchService'
import CategoryService from '../../services/categoryService'

const ProductMarginFilters = ({ filters, onFiltersChange, onClear }) => {
  const [branches, setBranches] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  
  // Load branches and categories from API
  useEffect(() => {
    const loadFilters = async () => {
      try {
        setLoading(true)
        console.log('🔍 Product Margin Report: Loading filters')
        
        // Load branches (only outlets)
        const branchResponse = await BranchService.getAllBranches()
        console.log('✅ Product Margin Report: Branches response:', branchResponse)
        
        let branchesData = []
        if (branchResponse && branchResponse.success) {
          if (branchResponse.data && branchResponse.data.branches && Array.isArray(branchResponse.data.branches)) {
            branchesData = branchResponse.data.branches
          } else if (Array.isArray(branchResponse.data)) {
            branchesData = branchResponse.data
          }
        }
        
        // Filter to only show outlet branches (exclude warehouses)
        const outletBranches = branchesData.filter(branch => branch.type === 'outlet')
        console.log('🏪 Product Margin Report: Filtered to outlets only:', outletBranches.length, 'branches')
        setBranches(outletBranches)
        
        // Load categories
        const categoryResponse = await CategoryService.getAllCategories()
        console.log('✅ Product Margin Report: Categories response:', categoryResponse)
        
        let categoriesData = []
        if (categoryResponse && categoryResponse.success) {
          if (categoryResponse.data && categoryResponse.data.categories && Array.isArray(categoryResponse.data.categories)) {
            categoriesData = categoryResponse.data.categories
          } else if (Array.isArray(categoryResponse.data)) {
            categoriesData = categoryResponse.data
          }
        }
        
        // Filter to only active categories
        const activeCategories = categoriesData.filter(category => category.isActive)
        console.log('📂 Product Margin Report: Active categories:', activeCategories.length, 'categories')
        setCategories(activeCategories)
        
      } catch (error) {
        console.error('❌ Product Margin Report: Error loading filters:', error)
      } finally {
        setLoading(false)
      }
    }
    
    loadFilters()
  }, [])
  
  // Mock branches as fallback for branches only
  const mockBranches = useMemo(() => [
    { _id: '68ab4a5075017ad6d7d8ad53', name: 'Tezlaa Colombo', type: 'outlet', isActive: true },
    { _id: '68ab4a8275017ad6d7d8ad5b', name: 'Tezlaa Negombo', type: 'outlet', isActive: true }
  ], [])
  
  // Display branches (real or mock), categories always from API/service
  const displayBranches = branches.length > 0 ? branches : mockBranches
  const displayCategories = categories // Always use categories from CategoryService
  
  const handleFilterChange = (key, value) => {
    onFiltersChange({ ...filters, [key]: value })
  }

  const handleClearFilters = () => {
    // Clear filters using BaseReportPage's onClear callback if available
    if (onClear) {
      onClear()
    } else {
      // Fallback: clear filters manually
      onFiltersChange({})
    }
  }

  const hasActiveFilters = filters.branch || filters.category
  
  return (
    <div className="bg-gray-50 p-4 rounded-lg mb-4">
      <div className="flex justify-between items-center mb-3">
        <h3 className="text-lg font-medium text-gray-900">Filters</h3>
        {hasActiveFilters && (
          <button
            onClick={handleClearFilters}
            className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-orange-600 bg-white border border-orange-300 rounded-lg hover:bg-orange-50 hover:text-orange-700 hover:border-orange-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 transition-colors"
          >

            <span>Clear Filters</span>
          </button>
        )}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Branch Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Branch</label>
          <div className="relative">
            <select
              value={filters.branch || ''}
              onChange={(e) => handleFilterChange('branch', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Branches</option>
              {displayBranches.map((branch) => (
                <option key={branch._id} value={branch._id}>
                  {branch.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          {loading && <p className="text-xs text-gray-500 mt-1">Loading branches...</p>}
          {!loading && branches.length === 0 && <p className="text-xs text-amber-500 mt-1">Using default branches</p>}
        </div>
        
        {/* Category Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
          <div className="relative">
            <select
              value={filters.category || ''}
              onChange={(e) => handleFilterChange('category', e.target.value)}
              className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md text-sm appearance-none"
              disabled={loading}
            >
              <option value="">All Categories</option>
              {displayCategories.map((category) => (
                <option key={category._id} value={category._id}>
                  {category.name}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <ChevronDown className="h-4 w-4" />
            </div>
          </div>
          {loading && <p className="text-xs text-gray-500 mt-1">Loading categories...</p>}
          {!loading && categories.length === 0 && <p className="text-xs text-amber-500 mt-1">Using default categories</p>}
        </div>
      </div>
    </div>
  )
}

const ProductMarginReport = () => {
  const columns = [
    { key: 'code', label: 'Code', type: 'text', visible: true },
    { key: 'productName', label: 'Product name', type: 'text', visible: true },
    { key: 'category', label: 'Category', type: 'text', visible: true },
    { key: 'sellingPrice', label: 'Selling Price', type: 'currency', visible: true },
    { key: 'purchasePrice', label: 'Purchase price', type: 'currency', visible: true },
    { key: 'margin', label: 'Margin', type: 'currency', visible: true },
  ]

  return (
    <BaseReportPage
      reportType="productMargin"
      title="Product Margin Report"
      columns={columns}
      dataKey="data"
      enableFilters={true}
      customFilterComponent={ProductMarginFilters}
    />
  )
}

export default ProductMarginReport